import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const recommendations = [
  {
    id: 1,
    territory: 'Cebu City - Zone 3',
    region: 'Visayas',
    type: 'expansion',
    priority: 'high',
    title: 'Untapped Retail Density',
    description: 'AI detected 47 unserved retail outlets within 2km radius. Estimated monthly revenue potential of ₱380,000 with 2 additional salespeople.',
    impact: '₱380K/mo',
    effort: 'Medium',
    timeline: '4-6 weeks',
    actions: ['Assign 2 new salespeople', 'Create Zone 3A sub-territory', 'Prioritize sari-sari stores'],
    icon: 'MapPin',
    color: 'var(--color-success)',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  {
    id: 2,
    territory: 'Davao - Toril District',
    region: 'Mindanao',
    type: 'optimization',
    priority: 'high',
    title: 'Route Efficiency Gap',
    description: 'Current salesperson covers 34 accounts with 180km daily route. AI suggests restructuring to 2 routes of 17 accounts each, reducing travel by 40%.',
    impact: '+40% efficiency',
    effort: 'Low',
    timeline: '1-2 weeks',
    actions: ['Split territory into North/South routes', 'Reassign 17 accounts to new rep', 'Update visit schedules'],
    icon: 'Route',
    color: 'var(--color-primary)',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  {
    id: 3,
    territory: 'NCR - Quezon City North',
    region: 'NCR',
    type: 'retention',
    priority: 'medium',
    title: 'At-Risk Account Cluster',
    description: '12 accounts showing 3-month declining order frequency. Proactive engagement campaign could recover ₱220,000 monthly revenue.',
    impact: '₱220K recovery',
    effort: 'Medium',
    timeline: '2-3 weeks',
    actions: ['Schedule account visits', 'Offer loyalty promotions', 'Review credit terms'],
    icon: 'Users',
    color: 'var(--color-warning)',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  {
    id: 4,
    territory: 'Iloilo City',
    region: 'Visayas',
    type: 'seasonal',
    priority: 'medium',
    title: 'Fiesta Season Demand Spike',
    description: 'Historical data shows 65% sales increase during May-June fiesta season. Pre-positioning inventory and promotional bundles recommended.',
    impact: '+65% volume',
    effort: 'Low',
    timeline: '3 weeks',
    actions: ['Pre-stock 3 key SKUs', 'Create fiesta bundle promotions', 'Brief sales team on targets'],
    icon: 'Calendar',
    color: 'var(--color-secondary)',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
  },
];

const PRIORITY_BADGE = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

export default function TerritoryRecommendations() {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="card p-4 lg:p-6 table-panel-depth">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">AI Territory Recommendations</h3>
          <p className="font-caption text-xs text-muted-foreground mt-0.5">{recommendations?.length} actionable suggestions identified</p>
        </div>
        <div className="flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
          <Icon name="BrainCircuit" size={14} color="var(--color-secondary)" strokeWidth={2} />
          <span className="font-caption text-xs font-medium text-violet-700">AI Powered</span>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {recommendations?.map((rec) => (
          <div key={rec?.id} className={`border rounded-xl overflow-hidden transition-all duration-200 ${rec?.borderColor}`}>
            <button
              className={`w-full flex items-start gap-3 p-4 text-left ${rec?.bgColor} hover:opacity-90 transition-opacity`}
              onClick={() => setExpanded(expanded === rec?.id ? null : rec?.id)}
            >
              <div
                className="flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5"
                style={{ width: 36, height: 36, background: `${rec?.color}20` }}
              >
                <Icon name={rec?.icon} size={18} color={rec?.color} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-semibold text-sm text-foreground">{rec?.title}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${PRIORITY_BADGE?.[rec?.priority]}`}>
                    {rec?.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">{rec?.territory} · {rec?.region}</p>
                <p className="font-body text-xs text-foreground mt-1.5 line-clamp-2">{rec?.description}</p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="font-data text-sm font-semibold" style={{ color: rec?.color }}>{rec?.impact}</span>
                <span className="font-caption text-xs text-muted-foreground">{rec?.timeline}</span>
                <Icon name={expanded === rec?.id ? 'ChevronUp' : 'ChevronDown'} size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
              </div>
            </button>

            {expanded === rec?.id && (
              <div className="px-4 pb-4 bg-card border-t border-border">
                <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-3 mb-2">Recommended Actions</p>
                <div className="flex flex-col gap-1.5 mb-3">
                  {rec?.actions?.map((action, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${rec?.color}20` }}>
                        <span className="font-data text-xs font-semibold" style={{ color: rec?.color }}>{i + 1}</span>
                      </div>
                      <span className="font-body text-sm text-foreground">{action}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <Icon name="Zap" size={13} color="var(--color-muted-foreground)" strokeWidth={2} />
                    <span className="font-caption text-xs text-muted-foreground">Effort: <strong className="text-foreground">{rec?.effort}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Icon name="Clock" size={13} color="var(--color-muted-foreground)" strokeWidth={2} />
                    <span className="font-caption text-xs text-muted-foreground">Timeline: <strong className="text-foreground">{rec?.timeline}</strong></span>
                  </div>
                  <Button variant="default" size="xs" iconName="CheckCircle" iconPosition="left" iconSize={13} className="ml-auto">
                    Implement
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}